import React, { useEffect, useState } from "react";
import { Grid, TextField, MenuItem, Typography, Box } from "@mui/material";
import useStyles from "./style";
import { useDropzone } from "react-dropzone";
import { EditOutlined } from "@mui/icons-material";
import CountryCodeDropdown from "SharedComponents/CountryCodeDropdown";
import { getCountryCodes } from "Utils/country";

const BasicInformation = ({ onRef, formik, driverId, setsubmitButton, setSkipButton }) => {
  const classes = useStyles();

  useEffect(() => {
    setsubmitButton(true);
    setSkipButton(true);
  }, [setsubmitButton, setSkipButton]);

  const maxFileSize = 5242880;
  const formatBytes = (bytes, decimals = 2) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + " " + sizes[i];
  };

  const fileLengthValidator = (file) => {
    if (file.size > maxFileSize) {
      alert(`File size is larger than ${formatBytes(maxFileSize)}`);
      return {
        code: "file-too-large",
        message: `File size is larger than ${formatBytes(maxFileSize)}`,
      };
    }
    return null;
  };

  const { getRootProps, getInputProps, open } = useDropzone({
    noClick: true,
    noKeyboard: true,
    maxSize: formatBytes(maxFileSize),
    maxFiles: 1,
    accept: "image/*",
    validator: fileLengthValidator,
    onDrop: (acceptedFiles) => {
      if (acceptedFiles && acceptedFiles.length > 0) {
        const file = acceptedFiles[0];
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result;
          formik.setFieldValue("passengerProfileImagefileformat", file);
          formik.setFieldValue("passengerProfileImage", base64String);
        };
        reader.readAsDataURL(file);
      }
    },
  });

  const [countryCodeData, setCountryCodeData] = useState([]);

  useEffect(() => {
    try {
      const countries = getCountryCodes();
      setCountryCodeData(countries);
    } catch (error) {
      console.error("Error fetching country codes:", error);
    }
  }, []);

  return (
    <Box className={classes.BasicInformationStyles}>
      {/* Left Column – Profile Photo */}
      <Box className={classes.leftColumn}>
        <Box className={classes.avatarWrapper}>
          <img
            src={formik.values.passengerProfileImage ? formik.values.passengerProfileImage : "/assets/images/user/avatar.png"}
            alt="Profile"
            className={classes.avatarImg}
          />
          <Box
            component="span"
            className={classes.editIconOverlay}
            {...getRootProps()}
            onClick={open}
          >
            <input {...getInputProps()} />
            <EditOutlined />
          </Box>
        </Box>
        <Typography className={classes.photoLabel}>Profile Photo</Typography>
        <Typography className={classes.photoSubLabel}>JPG, PNG up to 5MB</Typography>
        {formik.errors.passengerProfileImage && formik.touched.passengerProfileImage && (
          <Typography color="error" variant="caption" style={{ marginTop: 4 }}>
            {formik.errors.passengerProfileImage}
          </Typography>
        )}
      </Box>

      {/* Right Column – Form */}
      <Box className={classes.rightColumn}>
        <Grid container spacing={2}>
          {/* First Name */}
          <Grid item xs={12} md={6}>
            <Typography className={classes.formLabel}>First Name</Typography>
            <TextField
              id="firstName"
              name="firstName"
              placeholder="e.g. John"
              value={formik.values.firstName}
              onChange={formik.handleChange}
              error={formik.touched.firstName && Boolean(formik.errors.firstName)}
              helperText={formik.touched.firstName && formik.errors.firstName}
              fullWidth
              size="small"
              variant="outlined"
              className={classes.formInput}
            />
          </Grid>

          {/* Last Name */}
          <Grid item xs={12} md={6}>
            <Typography className={classes.formLabel}>Last Name</Typography>
            <TextField
              id="lastName"
              name="lastName"
              placeholder="e.g. Doe"
              value={formik.values.lastName}
              onChange={formik.handleChange}
              error={formik.touched.lastName && Boolean(formik.errors.lastName)}
              helperText={formik.touched.lastName && formik.errors.lastName}
              fullWidth
              size="small"
              variant="outlined"
              className={classes.formInput}
            />
          </Grid>

          {/* Mobile Number */}
          <Grid item xs={12}>
            <Typography className={classes.formLabel}>Mobile Number</Typography>
            <Box className={classes.phoneRow}>
              <Box className={classes.countryCodeWrapper}>
                <CountryCodeDropdown
                  formik={formik}
                  countryCodeData={countryCodeData}
                  name="countryCode"
                  id="countryCode"
                  label=""
                  error={formik.touched.countryCode && Boolean(formik.errors.countryCode)}
                  helperText={formik.touched.countryCode && formik.errors.countryCode}
                  roundedInputStyle={{}}
                />
              </Box>
              <TextField
                name="mobile"
                id="mobile"
                placeholder="000 000 0000"
                value={formik.values.mobile}
                onChange={formik.handleChange}
                error={formik.touched.mobile && Boolean(formik.errors.mobile)}
                helperText={formik.touched.mobile && formik.errors.mobile}
                fullWidth
                size="small"
                variant="outlined"
                className={classes.formInput}
              />
            </Box>
          </Grid>

          {/* Email */}
          <Grid item xs={12}>
            <Typography className={classes.formLabel}>Email Address</Typography>
            <TextField
              id="email"
              name="email"
              placeholder="john.doe@example.com"
              value={formik.values.email}
              onChange={formik.handleChange}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              fullWidth
              size="small"
              variant="outlined"
              className={classes.formInput}
            />
          </Grid>

          {/* Gender */}
          <Grid item xs={12} md={6}>
            <Typography className={classes.formLabel}>Gender</Typography>
            <TextField
              name="gender"
              id="gender"
              select
              value={formik.values.gender || ""}
              onChange={formik.handleChange}
              error={formik.touched.gender && Boolean(formik.errors.gender)}
              helperText={formik.touched.gender && formik.errors.gender}
              fullWidth
              size="small"
              variant="outlined"
              className={classes.formInput}
              SelectProps={{
                displayEmpty: true,
                renderValue: (v) => v ? (v.charAt(0).toUpperCase() + v.slice(1)) : <span style={{ color: "#9ca3af" }}>Select gender</span>,
              }}
            >
              {["male", "female"].map((option) => (
                <MenuItem key={option} value={option}>
                  {option.charAt(0).toUpperCase() + option.slice(1)}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {/* Country */}
          <Grid item xs={12} md={6}>
            <Typography className={classes.formLabel}>Country</Typography>
            <TextField
              name="country"
              id="country"
              select
              value={formik.values.country || ""}
              onChange={formik.handleChange}
              error={formik.touched.country && Boolean(formik.errors.country)}
              helperText={formik.touched.country && formik.errors.country}
              fullWidth
              size="small"
              variant="outlined"
              className={classes.formInput}
              SelectProps={{
                displayEmpty: true,
                renderValue: (v) => v || <span style={{ color: "#9ca3af" }}>Select country</span>,
              }}
            >
              {countryCodeData.map((country) => (
                <MenuItem key={country.code} value={country.name}>
                  {`${country.code} (${country.name})`}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default BasicInformation;
